
import React from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { MapPin, Package, Building2, Truck, AlertTriangle, Loader2 } from "lucide-react";
import { SearchResult, InventoryItem } from "@/types/inventory";
import { toWordCase, toSentenceCase, formatCityName } from "@/lib/textUtils";
import TooManyResultsError from "@/components/TooManyResultsError";

interface SearchTermCardProps {
  result: SearchResult;
  itemsAwaitingStoreData?: Set<string>;
}

const SearchTermCard: React.FC<SearchTermCardProps> = ({ result, itemsAwaitingStoreData = new Set() }) => {
  console.log('SearchTermCard received result:', result);
  console.log('Too many results flag:', result.too_many_results);
  console.log('Items count:', result.items.length);
  console.log('Items awaiting store data:', Array.from(itemsAwaitingStoreData));

  // Helper function to format price consistently
  const formatPrice = (price: string | number): string => {
    if (typeof price === 'number') {
      return `$${price.toFixed(2)}`;
    }
    if (typeof price === 'string' && price.trim() !== '') {
      // Remove any existing dollar signs and spaces, then add dollar sign
      const cleanPrice = price.replace(/[$\s]/g, '');
      // Handle empty string after cleaning
      if (cleanPrice === '' || cleanPrice === '0') {
        return '$0.00';
      }
      // Add .00 if no decimal point exists
      if (!cleanPrice.includes('.')) {
        return `$${cleanPrice}.00`;
      }
      return `$${cleanPrice}`;
    }
    return '$0.00';
  };

  // Handle too many results case FIRST - this takes priority over everything else
  if (result.too_many_results) {
    console.log('Displaying too many results error');
    return (
      <TooManyResultsError 
        searchTerm={result.searchTerm}
        totalItemsFromAPI={result.totalItemsFromAPI}
        maxAllowedItems={result.maxAllowedItems}
        context="search"
      />
    );
  }

  // If no items found at all and not too many results
  if (result.items.length === 0 && !result.too_many_results) {
    return (
      <Card className="overflow-hidden animate-fade-in">
        <CardHeader className="bg-muted/50">
          <CardTitle className="text-lg font-semibold">
            {toSentenceCase(result.searchTerm)}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 text-center text-muted-foreground">
          No results. Either nothing is in stock or on order, or your search didn't match any items.
        </CardContent>
      </Card>
    );
  }

  // Show all items immediately, regardless of store data status
  return (
    <div className="space-y-4">
      {result.items.map((item) => {
        const isAwaitingStoreData = itemsAwaitingStoreData.has(item.id);
        const hasStock = item.storeQty > 0;
        const hasStoreData = item.stores && item.stores.length > 0 && 
                           !(item.stores.length === 1 && item.stores[0].storeNumber === "No data available in table");
        
        console.log(`Rendering item: ${item.name}, storeQty: ${item.storeQty}, awaiting: ${isAwaitingStoreData}, hasStoreData: ${hasStoreData}`);
        
        return (
          <Card
            key={item.id}
            className={`overflow-hidden animate-fade-in ${hasStock ? 'border-whiskey-amber' : ''}`}
          >
            <CardHeader className={hasStock ? "bg-whiskey-amber/20" : "bg-muted/50"}>
              <div className="space-y-3">
                {/* Product name - full width, left aligned */}
                <CardTitle className="text-lg font-semibold text-left w-full">
                  {toWordCase(item.name)}
                </CardTitle>
                
                {/* Price - smaller font, lighter color, explicitly left aligned for all views */}
                <div className="text-sm font-medium text-muted-foreground text-left">
                  {formatPrice(item.price)}
                </div>
                
                {/* Inventory levels in list format */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Warehouse:</span>
                    <Badge variant="blue-compliant">
                      {item.warehouseQty}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">On Order:</span>
                    <Badge variant="purple-compliant">
                      {item.onOrderQty}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">In Stores:</span>
                    {isAwaitingStoreData ? (
                      <Badge variant="yellow-compliant">
                        Searching stores
                      </Badge>
                    ) : hasStock ? (
                      <Badge variant="green-compliant">
                        {item.storeQty}
                      </Badge>
                    ) : (
                      <Badge variant="red-compliant">
                        0
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-4">
              {isAwaitingStoreData ? (
                <div className="flex items-center justify-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Searching for store inventory levels...
                </div>
              ) : hasStoreData ? (
                <div className="text-sm">
                  {/* Mobile-first responsive table */}
                  <div className="block md:hidden space-y-3">
                    {/* Mobile: Card-style layout for each store */}
                    {item.stores.map((store, index) => (
                      <div key={index} className="p-3 border rounded-lg bg-muted/20">
                        <div className="flex justify-between items-start mb-2">
                          <div className="font-medium text-sm">
                            {formatCityName(store.city)}
                          </div>
                          <div className="font-medium text-sm">
                            {store.qty}
                          </div>
                        </div>
                        <div className="text-xs text-muted-foreground space-y-1">
                          <div className="break-words font-medium">
                            {toWordCase(store.address)}
                          </div>
                          <div>
                            Store {store.storeNumber?.substring(2)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Desktop: Traditional table layout with proper alignment */}
                  <div className="hidden md:block overflow-x-auto">
                    <Table className="w-full">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs text-left min-w-[60px]">Store</TableHead>
                          <TableHead className="text-xs text-left min-w-[200px]">Address</TableHead>
                          <TableHead className="text-xs text-left min-w-[100px]">City</TableHead>
                          <TableHead className="text-xs text-left min-w-[60px]">Qty</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {item.stores.map((store, index) => (
                          <TableRow key={index}>
                            <TableCell className="font-medium text-xs p-2 text-left">
                              {store.storeNumber?.substring(2)}
                            </TableCell>
                            <TableCell className="text-xs p-2 break-words text-left max-w-[200px]">
                              {toWordCase(store.address)}
                            </TableCell>
                            <TableCell className="text-xs p-2 text-left">
                              {formatCityName(store.city)}
                            </TableCell>
                            <TableCell className="font-semibold text-xs p-2 text-left">
                              {store.qty}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ) : item.storeQty === 0 ? (
                <div className="text-center text-muted-foreground">
                  Not available at any stores currently.
                </div>
              ) : null}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default SearchTermCard;
